import {useCallback, useState} from 'react'

interface ConnectionState {
  identity: string
  connectRequested: boolean
  wasLive: boolean
  retryUsed: boolean
  failedGeneration?: number
  attemptGeneration: number
}

interface ConnectionOptions {
  identity: string
  pendingIdentity?: string
  flagEnabled: boolean
  eligible?: boolean
  autoConnect: boolean
  isLive: boolean
  hasFailed: boolean
  canRetry: boolean
}

export function useDeferredSandboxConnection({
  identity,
  pendingIdentity,
  flagEnabled,
  eligible,
  autoConnect,
  isLive,
  hasFailed,
  canRetry,
}: ConnectionOptions) {
  const initialState = (): ConnectionState => ({
    identity,
    connectRequested: false,
    wasLive: false,
    retryUsed: false,
    attemptGeneration: 0,
  })
  const [state, setState] = useState(initialState)
  if (state.identity !== identity) {
    setState({
      ...initialState(),
      connectRequested: state.identity === pendingIdentity && state.connectRequested && !state.wasLive,
    })
  } else {
    const connectRequested = eligible === false ? false : state.connectRequested
    const firstAttempt =
      flagEnabled && eligible === true && !state.attemptGeneration && (autoConnect || connectRequested || state.wasLive)
    const failedGeneration = hasFailed ? (state.failedGeneration ?? state.attemptGeneration) : undefined
    if (
      connectRequested !== state.connectRequested ||
      (isLive && (!state.wasLive || state.retryUsed)) ||
      failedGeneration !== state.failedGeneration ||
      firstAttempt
    ) {
      setState({
        ...state,
        connectRequested,
        wasLive: state.wasLive || isLive,
        retryUsed: isLive ? false : state.retryUsed,
        failedGeneration,
        attemptGeneration: state.attemptGeneration + (firstAttempt ? 1 : 0),
      })
    }
  }
  const current = state.identity === identity ? state : initialState()
  const phase = isLive
    ? 'live'
    : hasFailed && current.failedGeneration === current.attemptGeneration
      ? 'failed'
      : eligible &&
          (!flagEnabled || autoConnect || current.connectRequested || current.wasLive || current.attemptGeneration > 0)
        ? 'connecting'
        : 'deferred'
  const canRequestRetry =
    flagEnabled && phase === 'failed' && current.wasLive && !current.retryUsed && canRetry && eligible === true
  const requestConnection = useCallback(() => {
    if (
      !identity ||
      !flagEnabled ||
      (phase !== 'deferred' && !canRequestRetry) ||
      (current.connectRequested && eligible !== true)
    )
      return
    setState(previous => {
      if (previous.identity !== identity || isLive) return previous
      if (hasFailed && previous.failedGeneration === previous.attemptGeneration) {
        if (!previous.wasLive || previous.retryUsed || !canRetry || eligible !== true) return previous
        return {
          ...previous,
          retryUsed: true,
          connectRequested: true,
          attemptGeneration: previous.attemptGeneration + 1,
        }
      }
      if (
        eligible &&
        (!flagEnabled || autoConnect || previous.connectRequested || previous.wasLive || previous.attemptGeneration > 0)
      )
        return previous
      if (eligible === false || previous.connectRequested) return previous
      return {
        ...previous,
        connectRequested: true,
        ...(eligible ? {attemptGeneration: previous.attemptGeneration + 1} : {}),
      }
    })
  }, [
    autoConnect,
    canRequestRetry,
    canRetry,
    current.connectRequested,
    eligible,
    flagEnabled,
    hasFailed,
    identity,
    isLive,
    phase,
  ])

  return {
    phase,
    connectEnabled: !flagEnabled || (eligible === true && phase !== 'deferred'),
    attemptGeneration: current.attemptGeneration,
    canRequestRetry,
    wasLive: current.wasLive,
    requestConnection,
  }
}
